import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { getMenuItems } from "services/menu/rest";
import { MenuCategoryDto, MenuItemListDto } from "services/menu/restModels";

import { useDebouncedValue } from "shared/hooks/useDebouncedValue";
import { matchesLocalizedQuery } from "shared/lib/localizedMap";

import { PICKER_PAGE_SIZE, SEARCH_DEBOUNCE_MS } from "features/StopList/constants";
import { StopListMutations } from "features/StopList/hooks/useStopListMutations";
import { PickerMode } from "features/StopList/types";

export interface UseStopListPickerParams {
    menuId: number;
    open: boolean;
    categories: MenuCategoryDto[];
    mutations: StopListMutations;
    setCategoryStopList: (menuCategoryId: number, isInStopList: boolean) => void;
}

export interface StopListPickerState {
    mode: PickerMode;
    setMode: (mode: PickerMode) => void;
    query: string;
    setQuery: (value: string) => void;

    items: MenuItemListDto[];
    itemsCount: number;
    itemsLoading: boolean;
    itemsError: boolean;
    hasMore: boolean;
    loadMore: () => void;

    categories: MenuCategoryDto[];
    categoriesCount: number;

    justAddedItemIds: Set<number>;
    justAddedCategoryIds: Set<number>;
    pendingItemIds: Set<number>;
    pendingCategoryIds: Set<number>;

    toggleItem: (item: MenuItemListDto) => void;
    toggleCategory: (category: MenuCategoryDto) => void;

    // Диалог применяет изменения сразу, поэтому при закрытии страница обновляет списки —
    // но только если было что применять.
    dirty: boolean;
}

const toggleError = (isInStopList: boolean): string =>
    isInStopList ? "Не удалось убрать из продажи" : "Не удалось отменить";

const withToggled = (current: Set<number>, id: number, present: boolean): Set<number> => {
    const next = new Set(current);
    if (present) {
        next.add(id);
    } else {
        next.delete(id);
    }
    return next;
};

/*
* Диалог «Убрать из продажи»: список кандидатов и применение прямо из него.
* Убранная строка из списка не исчезает, а переключается в состояние «в стоп-листе» с
* отменой — во время утренней приёмки диалог не должен закрываться из-за опечатки.
* */
export const useStopListPicker = (params: UseStopListPickerParams): StopListPickerState => {
    const { menuId, open, categories, mutations, setCategoryStopList } = params;

    const [mode, setMode] = useState<PickerMode>("items");
    const [query, setQuery] = useState<string>("");

    const [items, setItems] = useState<MenuItemListDto[]>([]);
    const [serverTotal, setServerTotal] = useState<number>(0);
    const [itemsLoading, setItemsLoading] = useState<boolean>(true);
    const [itemsError, setItemsError] = useState<boolean>(false);
    const [loadingMore, setLoadingMore] = useState<boolean>(false);

    const [justAddedItemIds, setJustAddedItemIds] = useState<Set<number>>(new Set());
    const [justAddedCategoryIds, setJustAddedCategoryIds] = useState<Set<number>>(new Set());
    const [pendingItemIds, setPendingItemIds] = useState<Set<number>>(new Set());
    const [pendingCategoryIds, setPendingCategoryIds] = useState<Set<number>>(new Set());
    const [dirty, setDirty] = useState<boolean>(false);

    const scopeRef = useRef<number>(0);

    const debouncedQuery = useDebouncedValue(query, SEARCH_DEBOUNCE_MS);
    // Пустой запрос применяем сразу: иначе после очистки поиска список ещё 300 мс живёт
    // в режиме поиска и успевает сходить за страницей дважды.
    const effectiveQuery = query.trim() === "" ? "" : debouncedQuery.trim();

    useEffect(() => {
        if (open) return;

        setQuery("");
        setMode("items");
        setJustAddedItemIds(new Set());
        setJustAddedCategoryIds(new Set());
        setDirty(false);
    }, [open]);

    useEffect(() => {
        if (!open) return;

        const scope = scopeRef.current + 1;
        scopeRef.current = scope;

        setItemsLoading(true);
        setItemsError(false);

        getMenuItems({
            menuId,
            query: effectiveQuery || undefined,
            filter: "notInStopList",
            skip: 0,
            limit: PICKER_PAGE_SIZE,
        })
            .then((result) => {
                if (scopeRef.current !== scope) return;

                if (!result) {
                    setItemsError(true);
                    setItems([]);
                    setServerTotal(0);
                    return;
                }

                setItems(result.items);
                setServerTotal(result.total ?? 0);
            })
            .catch(() => {
                if (scopeRef.current !== scope) return;
                setItemsError(true);
                setItems([]);
                setServerTotal(0);
            })
            .finally(() => {
                if (scopeRef.current !== scope) return;
                setItemsLoading(false);
            });
    }, [menuId, effectiveQuery, open]);

    /*
    * Убранные из этого же диалога позиции остаются в списке, но из серверной выборки уже
    * выпали. Поэтому skip считаем по строкам, которые в ней ещё есть, иначе каждая догрузка
    * перепрыгивала бы через столько позиций, сколько убрано.
    * */
    const loadMore = useCallback(() => {
        if (itemsLoading || loadingMore || items.length >= serverTotal) return;

        const scope = scopeRef.current;
        setLoadingMore(true);

        getMenuItems({
            menuId,
            query: effectiveQuery || undefined,
            filter: "notInStopList",
            skip: items.length - justAddedItemIds.size,
            limit: PICKER_PAGE_SIZE,
        })
            .then((result) => {
                if (scopeRef.current !== scope || !result) return;
                setItems((current) => [...current, ...result.items]);
            })
            .catch(() => undefined)
            .finally(() => {
                if (scopeRef.current !== scope) return;
                setLoadingMore(false);
            });
    }, [
        menuId,
        effectiveQuery,
        items.length,
        justAddedItemIds.size,
        serverTotal,
        itemsLoading,
        loadingMore,
    ]);

    const toggleItem = useCallback(
        (item: MenuItemListDto) => {
            const isInStopList = !justAddedItemIds.has(item.id);

            setPendingItemIds((current) => withToggled(current, item.id, true));

            mutations
                .setItemInStopList(item.id, isInStopList, { error: toggleError(isInStopList) })
                .then((ok) => {
                    if (!ok) return;

                    setJustAddedItemIds((current) => withToggled(current, item.id, isInStopList));
                    setDirty(true);
                })
                .finally(() => {
                    setPendingItemIds((current) => withToggled(current, item.id, false));
                });
        },
        [justAddedItemIds, mutations]
    );

    const toggleCategory = useCallback(
        (category: MenuCategoryDto) => {
            const isInStopList = !justAddedCategoryIds.has(category.id);

            setPendingCategoryIds((current) => withToggled(current, category.id, true));

            mutations
                .setCategoryInStopList(category, isInStopList, { error: toggleError(isInStopList) })
                .then((ok) => {
                    if (!ok) return;

                    setJustAddedCategoryIds((current) =>
                        withToggled(current, category.id, isInStopList)
                    );
                    setCategoryStopList(category.id, isInStopList);
                    setDirty(true);
                })
                .finally(() => {
                    setPendingCategoryIds((current) => withToggled(current, category.id, false));
                });
        },
        [justAddedCategoryIds, mutations, setCategoryStopList]
    );

    const visibleCategories = useMemo(
        () =>
            categories.filter(
                (category) =>
                    (!category.isInStopList || justAddedCategoryIds.has(category.id)) &&
                    matchesLocalizedQuery(category.name, query)
            ),
        [categories, justAddedCategoryIds, query]
    );

    return {
        mode,
        setMode,
        query,
        setQuery,

        items,
        itemsCount: Math.max(0, serverTotal - justAddedItemIds.size),
        itemsLoading,
        itemsError,
        hasMore: items.length < serverTotal,
        loadMore,

        categories: visibleCategories,
        categoriesCount: visibleCategories.filter((category) => !category.isInStopList).length,

        justAddedItemIds,
        justAddedCategoryIds,
        pendingItemIds,
        pendingCategoryIds,

        toggleItem,
        toggleCategory,

        dirty,
    };
};
